import React from "react";
import { useUpdateJobMutation } from "~/shared/api/jobs/job.grpc";
import { cancelledJobUpdate } from "~/shared/lib/job-status";
import { ConnectProto } from "@racoon/api";
import { OrderRow } from "./adapters";

const toJobId = (id: number | string): bigint | null => {
  if (typeof id === "number") {
    return Number.isFinite(id) ? BigInt(id) : null;
  }
  return /^\d+$/.test(id) ? BigInt(id) : null;
};

export const useOrderActions = () => {
  const [updateJob, { isLoading }] = useUpdateJobMutation();

  const handleToggleAiVisibility = React.useCallback(
    async (e: React.MouseEvent, row: OrderRow) => {
      e.stopPropagation();
      if (row._isGroupHeader) return;
      const jobId = toJobId(row.id);
      if (jobId === null) {
        console.error("Failed to toggle AI visibility: invalid job id", row.id);
        return;
      }
      const newValue = !row.visible_to_ai;
      try {
        const updateData: any = { id: jobId, visibleToAi: newValue };
        if (row.service_provider_id) updateData.serviceProviderId = BigInt(row.service_provider_id);
        if (row.service_receiver?.id)
          updateData.serviceReceiverId = BigInt(row.service_receiver.id);
        await updateJob(updateData).unwrap();
      } catch (error) {
        console.error("Failed to toggle AI visibility", error);
      }
    },
    [updateJob],
  );

  const handleCancelOrder = React.useCallback(
    async (job: ConnectProto.Job): Promise<boolean> => {
      try {
        await updateJob(cancelledJobUpdate(job)).unwrap();
        return true;
      } catch {
        return false;
      }
    },
    [updateJob],
  );

  return {
    handleToggleAiVisibility,
    handleCancelOrder,
    isLoading,
  };
};
